import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@/components/ui/input-otp";
import { useRegistration } from "@/context/RegistrationContext";
import { sendOtp, verifyApplication, getRegistration, verifyOtp, setCandidateToken, consumeNeedsOtpReauth, OTP_REAUTH_TIP } from "@/lib/api";
import { maskEmail, maskName, maskPhone } from "@/lib/maskPii";
import {
  ShieldCheck,
  CheckCircle2,
  Info,
  Phone,
  Sparkles,
  Loader2,
  Mail,
  XCircle,
  Timer,
  RefreshCcw,
  PencilLine,
} from "lucide-react";
import { toast } from "sonner";

function VerifyDetailRow({ label, value, testId }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 min-w-0" data-testid={testId}>
      <span className="text-[#6B7280] shrink-0 w-[4.5rem]">{label}</span>
      <span className="font-semibold text-[#111] truncate">{value}</span>
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const { state, update } = useRegistration();
  const [appNo, setAppNo] = useState(state.applicationNumber || "");
  const [mobile, setMobile] = useState(state.mobile || "");
  const [otpSent, setOtpSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyRes, setVerifyRes] = useState(null); // masked pre-login verify payload
  // If this application has already completed OTP verification once, the mobile is
  // locked server-side — UI only shows last-4 digits (no full number pre-login).
  const [lockedMobile, setLockedMobile] = useState(false);
  // Where OTP success should route to — known up front from the app-number
  // lookup, so we never have to race the /register page's own (slower)
  // backend-hydration redirect. Only a whole-application reupload needs the
  // full wizard; a single flagged document or policy is handled entirely in
  // the dashboard, and a fully clean/verified application goes to the ACK page.
  const [postOtpPath, setPostOtpPath] = useState("/register");

  // Inline OTP-entry state (folded in from the old separate /otp page).
  const [otpValue, setOtpValue] = useState("");
  const [seconds, setSeconds] = useState(59);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!otpSent || seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpSent, seconds]);

  // After a portal update / expired session, remind users to re-verify OTP.
  useEffect(() => {
    if (consumeNeedsOtpReauth()) {
      toast.info(OTP_REAUTH_TIP, { duration: 10000 });
    }
  }, []);

  const hasRegistration = !!verifyRes?.has_registration;
  const appVerified = verifyRes?.exists === true || hasRegistration;
  // Only gate brand-new registrations on offer-letter status — a candidate who
  // already has a registration row in our own system is never retroactively blocked.
  const offerNotIssued = verifyRes?.exists === true && !verifyRes?.eligible && !hasRegistration;
  // Withdrawn: roster admission_status for not-started; registrations.status once a row exists.
  const withdrawn =
    verifyRes?.admission_status === "withdrawn" ||
    (hasRegistration && verifyRes?.admission_status === "withdrawn");
  const mobileValid = lockedMobile || /^\+?\d{10,13}$/.test(mobile.replace(/\s/g, ""));

  // Keep local inputs in sync when ?app= seeds context on refresh / deep-link.
  useEffect(() => {
    if (state.applicationNumber && state.applicationNumber !== appNo) {
      setAppNo(state.applicationNumber);
    }
  }, [state.applicationNumber, appNo]);

  useEffect(() => {
    if (state.mobile && state.mobile !== mobile && !otpSent) {
      setMobile(state.mobile);
    }
  }, [state.mobile, mobile, otpSent]);

  // Debounced verification: hits backend when application number reaches min length.
  // Pre-OTP: only the masked verify endpoint — never GET /registrations/{id}.
  useEffect(() => {
    const clean = appNo.trim();
    setVerifyRes(null);
    setLockedMobile(false);
    setPostOtpPath("/register");
    if (clean.length < 4) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setVerifying(true);
      const r = await verifyApplication(clean);
      if (cancelled) return;
      setVerifying(false);
      setVerifyRes(r || { exists: false });
      let path = "/register";
      if (r?.submitted) path = "/dashboard";
      setPostOtpPath(path);
      if (r?.otp_verified && r?.mobile_last4) {
        setLockedMobile(true);
        setMobile(`••••••${r.mobile_last4}`);
      }
      if (r?.exists) update({ qid: r.qid || null, skipPolicies: !!r.skip_policies });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [appNo]);

  const resetOtpFlow = () => {
    if (lockedMobile) return;
    setOtpSent(false);
    setOtpValue("");
  };

  const editNumber = () => resetOtpFlow();

  const handleSendOtp = async () => {
    if (!appVerified) {
      toast.error(verifyRes?.exists === false ? "ID not found" : "Please enter a valid application or employee ID");
      return;
    }
    if (offerNotIssued) {
      toast.error("Your admission offer letter hasn't been issued yet — please contact the Admissions Office.");
      return;
    }
    if (withdrawn) {
      toast.error("This application has been withdrawn — please contact the Admissions Office.");
      return;
    }
    if (!mobileValid) {
      toast.error("Enter a valid mobile number");
      return;
    }
    // Locked returning candidates: server uses stored mobile; placeholder is fine.
    const cleanMobile = lockedMobile ? "0000000000" : mobile.replace(/[^\d]/g, "");
    const program = verifyRes?.program || "";
    const specialization = verifyRes?.specialization || "";
    const school = verifyRes?.school || "";
    const programSpec = [program, specialization].filter(Boolean).join(" - ");
    update({
      applicationNumber: appNo.trim(),
      mobile: lockedMobile ? "" : cleanMobile,
      qid: verifyRes?.qid || null,
      skipPolicies: !!verifyRes?.skip_policies,
      courseInfo: {
        program: program || null,
        specialization: specialization || null,
        school: school || null,
        display: [programSpec, school].filter(Boolean).join(", ") || null,
      },
    });
    setSending(true);
    const res = await sendOtp(cleanMobile, appNo.trim());
    setSending(false);
    if (!res.ok) {
      resetOtpFlow();
      const msg = res.data?.detail || "Could not send OTP — please try again";
      toast.error(msg, { duration: 6000 });
      return;
    }
    setOtpSent(true);
    setOtpValue("");
    setSeconds(59);
    if (res.data?.delivered) {
      toast.success("OTP sent to your mobile number");
    } else {
      toast.error(res.data?.message || "SMS could not be delivered — please try again", { duration: 8000 });
    }
  };

  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setVerifyingOtp(true);
    const cleanMobile = lockedMobile ? "0000000000" : mobile.replace(/[^\d]/g, "");
    const { ok, data } = await verifyOtp(cleanMobile, otpValue, appNo.trim());
    setVerifyingOtp(false);
    if (!ok) {
      if (data?.detail?.includes?.("already linked")) resetOtpFlow();
      toast.error(data?.detail || "Incorrect OTP");
      return;
    }
    if (data?.access_token) setCandidateToken(data.access_token);
    const realMobile = data?.mobile || (!lockedMobile ? cleanMobile : "");
    update({
      otpVerified: true,
      applicationNumber: appNo.trim(),
      mobile: realMobile,
    });
    toast.success("Mobile verified");
    const reg = await getRegistration(appNo.trim());
    if (reg?.submitted) {
      nav("/dashboard");
      return;
    }
    nav(postOtpPath);
  };

  const resendOtp = async () => {
    setResending(true);
    const cleanMobile = lockedMobile ? "0000000000" : mobile.replace(/[^\d]/g, "");
    const res = await sendOtp(cleanMobile, appNo.trim());
    setResending(false);
    setSeconds(59);
    setOtpValue("");
    if (!res.ok) {
      resetOtpFlow();
      toast.error(res.data?.detail || "Could not resend OTP", { duration: 6000 });
      return;
    }
    if (res.data?.delivered) {
      toast.success("New OTP sent to your mobile");
    } else {
      toast.error(res.data?.message || "SMS could not be delivered — please try again", { duration: 8000 });
    }
  };

  return (
    <div className="min-h-screen bg-white grid grid-cols-1 lg:grid-cols-2">
      {/* Left: brand pane */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#B60F2A]">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.25) 0%, transparent 45%)",
          }}
        />
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1781032161857-41214c66559f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzgzMzUxMDE4fDA&ixlib=rb-4.1.0&q=85"
            alt="Mahindra University Campus"
            className="w-full h-full object-cover opacity-25 mix-blend-luminosity"
          />
        </div>
        <div className="relative p-10 flex items-center gap-3">
          <div className="bg-white rounded-xl p-2">
            <Logo className="h-8" />
          </div>
          <div className="text-white/90 text-xs uppercase tracking-[0.15em] font-semibold">
            Registration Portal · 2026
          </div>
        </div>
        <div className="relative p-10 md:p-14">
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-white text-4xl md:text-5xl font-bold leading-tight tracking-tight"
          >
            Your journey at Mahindra begins here.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white/80 mt-4 max-w-md text-lg"
          >
            Complete your registration for the 2026 batch.
          </motion.p>
        </div>
        <div className="relative p-10 text-white/60 text-xs">
          &ldquo;Global Thinkers. Engaged Leaders.&rdquo;
        </div>
      </div>

      {/* Right: form */}
      <div className="flex flex-col min-h-screen">
        <div className="lg:hidden p-6 border-b border-[#E5E7EB] bg-white">
          <Logo withText />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-md"
          >
            <div className="inline-flex items-center gap-2 text-xs font-semibold bg-[#FBE7EA] text-[#B60F2A] px-3 py-1 rounded-full">
              <Sparkles className="h-3.5 w-3.5" />
              Registration Portal · 2026
            </div>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-[#111]">
              Start your registration
            </h2>
            <p className="text-[#6B7280] mt-2">
              Enter your application or employee ID and mobile to continue.
            </p>

            <div className="mu-card p-5 mt-6 bg-[#FBE7EA]/40 border-[#FBE7EA]" data-testid="mobile-notice">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-[#B60F2A] mt-0.5 shrink-0" />
                <div className="text-sm text-[#111] leading-relaxed space-y-2">
                  <p>
                    If the portal stopped responding after an update, please refresh
                    this page and verify OTP again to continue.
                  </p>
                  <p>
                    Please note that the mobile number you enter will be used for
                    all university-related communications until you graduate from
                    the university.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="mu-label" htmlFor="app-no">
                  {verifyRes?.skip_policies ? "Employee ID" : "Application / Employee ID"}
                </label>
                <div className="relative">
                  <input
                    id="app-no"
                    data-testid="input-application-number"
                    type="text"
                    value={appNo}
                    onChange={(e) => setAppNo(e.target.value.toUpperCase())}
                    placeholder={verifyRes?.skip_policies ? "e.g. EMP10234" : "e.g. MU20260012345 or EMP10234"}
                    className={`mu-input pr-28 ${otpSent ? "bg-[#F7F7F7] text-[#6B7280] cursor-not-allowed" : ""}`}
                    readOnly={otpSent}
                    aria-readonly={otpSent}
                  />
                  {verifying && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs font-medium text-[#6B7280] bg-[#F7F7F7] ring-1 ring-inset ring-[#E5E7EB] px-2 py-1 rounded-full">
                      <Loader2 className="h-3 w-3 animate-spin" /> Checking…
                    </span>
                  )}
                  {!verifying && appVerified && !offerNotIssued && !withdrawn && (
                    <span
                      className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2 py-1 rounded-full"
                      data-testid="badge-verified"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Verified
                    </span>
                  )}
                  {!verifying && offerNotIssued && (
                    <span
                      className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-50 ring-1 ring-inset ring-amber-200 px-2 py-1 rounded-full"
                      data-testid="badge-offer-pending"
                    >
                      <XCircle className="h-3.5 w-3.5" /> Offer letter pending
                    </span>
                  )}
                  {!verifying && withdrawn && (
                    <span
                      className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 ring-1 ring-inset ring-red-200 px-2 py-1 rounded-full"
                      data-testid="badge-withdrawn"
                    >
                      <XCircle className="h-3.5 w-3.5" /> Withdrawn
                    </span>
                  )}
                  {!verifying && verifyRes && verifyRes.exists === false && !hasRegistration && appNo.trim().length >= 4 && (
                    <span
                      className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 ring-1 ring-inset ring-red-200 px-2 py-1 rounded-full"
                      data-testid="badge-not-found"
                    >
                      <XCircle className="h-3.5 w-3.5" /> Not found
                    </span>
                  )}
                </div>
                {offerNotIssued && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="offer-pending-notice">
                    <Info className="h-3.5 w-3.5 text-amber-700 shrink-0" />
                    Your admission offer letter hasn&rsquo;t been issued yet. Please contact the Admissions Office before continuing.
                  </div>
                )}
                {withdrawn && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-red-900 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="withdrawn-notice">
                    <Info className="h-3.5 w-3.5 text-red-700 shrink-0" />
                    This application has been withdrawn. Please contact the Admissions Office if you believe this is a mistake.
                  </div>
                )}
                {appVerified && !offerNotIssued && !withdrawn && (verifyRes?.email || verifyRes?.name) && (
                  <div
                    className="mt-2 flex items-start gap-2 text-xs text-[#111] bg-green-50/60 border border-green-200 rounded-lg px-3 py-2.5"
                    data-testid="verified-email"
                  >
                    <Mail className="h-3.5 w-3.5 text-green-700 mt-0.5 shrink-0" />
                    <div className="min-w-0 space-y-1 flex-1">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-green-800/80">
                        Confirm these details belong to you
                      </p>
                      <VerifyDetailRow
                        label="Name"
                        value={verifyRes.pii_masked ? verifyRes.name : maskName(verifyRes.name)}
                        testId="verified-name"
                      />
                      <VerifyDetailRow
                        label="Email"
                        value={verifyRes.pii_masked ? verifyRes.email : maskEmail(verifyRes.email)}
                        testId="verified-email-value"
                      />
                      <VerifyDetailRow
                        label="Mobile"
                        value={verifyRes.pii_masked ? verifyRes.phone : maskPhone(verifyRes.phone)}
                        testId="verified-phone"
                      />
                      <VerifyDetailRow
                        label="Branch"
                        value={verifyRes.branch}
                        testId="verified-branch"
                      />
                      <VerifyDetailRow
                        label="School"
                        value={verifyRes.school}
                        testId="verified-school"
                      />
                      <VerifyDetailRow
                        label="Program"
                        value={[verifyRes.program, verifyRes.specialization].filter(Boolean).join(" · ")}
                        testId="verified-program"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="mu-label flex items-center gap-2" htmlFor="mobile">
                  Mobile Number
                  {lockedMobile && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-800 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                      <CheckCircle2 className="h-2.5 w-2.5" /> Previously verified · locked
                    </span>
                  )}
                </label>
                <div className="relative">
                  <Phone className="h-4 w-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    id="mobile"
                    data-testid="input-mobile"
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="+91 98XXXXXXXX"
                    className={`mu-input pl-10 ${lockedMobile || otpSent ? "bg-[#F7F7F7] text-[#6B7280] cursor-not-allowed" : ""}`}
                    readOnly={!!lockedMobile || otpSent}
                    aria-readonly={!!lockedMobile || otpSent}
                  />
                </div>
                {lockedMobile && (
                  <p className="text-[10px] text-[#6B7280] mt-1">
                    Your mobile was verified in a previous session — subsequent logins use the same number for security. Contact admissions to change it.
                  </p>
                )}
              </div>

              {!otpSent && (
                <div className="pt-2">
                  <button
                    type="button"
                    className="mu-btn-primary w-full"
                    onClick={handleSendOtp}
                    disabled={sending || !appVerified || !mobileValid || offerNotIssued || withdrawn}
                    data-testid="btn-send-otp"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                    Send OTP
                  </button>
                </div>
              )}

              {otpSent && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="pt-2 border-t border-[#F3F4F6] mt-2"
                  data-testid="otp-inline-section"
                >
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-[#111]">
                      Enter the 6-digit code sent to{" "}
                      <span className="font-medium">{mobile}</span>.
                    </p>
                    <button
                      type="button"
                      onClick={editNumber}
                      className="inline-flex items-center gap-1 text-xs text-[#B60F2A] hover:underline shrink-0"
                      data-testid="btn-edit-number"
                    >
                      <PencilLine className="h-3 w-3" /> Edit number
                    </button>
                  </div>

                  <div className="mt-4 flex justify-center">
                    <InputOTP
                      maxLength={6}
                      value={otpValue}
                      onChange={setOtpValue}
                      data-testid="otp-input"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup>
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-[#6B7280]" data-testid="otp-timer">
                      <Timer className="h-4 w-4" />
                      {seconds > 0 ? (
                        <span>
                          Resend in <span className="font-medium text-[#111]">00:{String(seconds).padStart(2, "0")}</span>
                        </span>
                      ) : (
                        <span>Didn&rsquo;t receive the code?</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={resendOtp}
                      disabled={seconds > 0 || resending}
                      className="inline-flex items-center gap-2 text-[#B60F2A] hover:underline disabled:opacity-40 disabled:no-underline"
                      data-testid="btn-resend-otp"
                    >
                      {resending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />} Resend OTP
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={verifyingOtp}
                    className="mu-btn-primary w-full mt-6"
                    data-testid="btn-verify-otp"
                  >
                    {verifyingOtp ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {verifyingOtp ? "Verifying…" : "Verify & Continue"}
                  </button>
                </motion.div>
              )}
            </div>

            <p className="text-xs text-[#9CA3AF] mt-6 leading-relaxed">
              By continuing you agree to Mahindra University&rsquo;s Terms of Service
              and Privacy Policy.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
